import React from 'react';
import {useAuthorization} from '../../../features/auth/hooks/useAuthorization';
import {useAuth} from '../../../features/auth/hooks/useAuth';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  width?: string | number;
  height?: string | number;
  variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost';
  fullWidth?: boolean;
  isLoading?: boolean;
  iconOnly?: boolean;
  /** Permission required to show this button */
  permission?: string;
  /** Permissions required (user needs ANY of these) */
  permissions?: string[];
  /** Whether user must have ALL permissions */
  requireAll?: boolean;
  /** Fallback content when user lacks permission */
  fallback?: React.ReactNode;
}

const sizeClasses = {
  small: 'h-8 text-base px-4',
  medium: 'h-10 text-lg px-5',
  large: 'h-12 text-xl px-6',
  xlarge: 'h-14 text-2xl px-8'
};

const variantClasses = {
  primary: 'bg-primary text-white hover:bg-primary/90',
  secondary: 'bg-secondary text-primary hover:bg-secondary/80',
  outline: 'bg-white text-primary border border-primary hover:bg-primary/10',
  danger: 'bg-error text-white hover:bg-error/90',
  ghost: 'bg-transparent text-primary hover:bg-primary/10'
};

const Button: React.FC<ButtonProps> = ({
  children = 'Button',
  className = '',
  icon,
  iconPosition = 'left',
  size = 'medium',
  width,
  height,
  variant = 'primary',
  fullWidth = false,
  isLoading = false,
  iconOnly = false,
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  disabled,
  ...props
}) => {
  const {hasPermission, hasAnyPermission} = useAuthorization();
  const {initialized: authInitialized} = useAuth();

  // Check permissions (backward compatible - no permission check if not specified)
  let hasAccess = true;

  // Only check permissions if they are specified
  if (permission) {
    // Don't show button if auth is not initialized and permission is required
    if (!authInitialized) {
      hasAccess = false;
    } else {
      hasAccess = hasPermission(permission);
    }
  } else if (permissions && permissions.length > 0) {
    // Don't show button if auth is not initialized and permissions are required
    if (!authInitialized) {
      hasAccess = false;
    } else if (requireAll) {
      hasAccess = permissions.every(perm => hasPermission(perm));
    } else {
      hasAccess = hasAnyPermission(permissions);
    }
  }
  // If no permissions specified, always show button (backward compatibility)

  // If user lacks permission, render fallback or nothing
  if (!hasAccess) {
    return fallback || null;
  }
  return (
    <button
      className={`
        flex items-center justify-center rounded-lg font-zain text-center leading-none transition-colors duration-150
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${fullWidth ? 'w-full' : ''}
        ${iconOnly ? 'p-2 w-10 h-10' : ''}
        ${disabled || isLoading ? 'opacity-60 cursor-not-allowed' : ''}
        ${className}
      `}
      style={{
        width: width || undefined,
        height: height || undefined,
        ...props.style
      }}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <span className="loader mr-3" />
      ) : icon && iconPosition === 'left' && (
        <span className="mr-3 flex items-center">{icon}</span>
      )}
      {!iconOnly && children}
      {icon && iconPosition === 'right' && !isLoading && (
        <span className="ml-3 flex items-center">{icon}</span>
      )}
      {/* Spinner style */}
      <style>{`
        .loader {
          border: 2px solid rgba(255,255,255,0.35);
          border-top: 2px solid var(--color-primary, #1a71db);
          border-radius: 50%;
          width: 18px;
          height: 18px;
          animation: spin 0.7s linear infinite;
          display: inline-block;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  );
};

export default Button;
